import {
  useState,
} from 'react';
import {
  Callout,
  Figure,
  Facts,
  Loading,
  Panel,
  TableScroll,
  formatCount,
  useLoad,
  SortTh,
  Sorted,
  sortableCell,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import {
  chain,
} from '../../screens.ts';
import { t, tCount } from '../../messages.ts';
import { Failure, Rich } from '../shared.tsx';
import { AuditRecordsDialog } from './AuditRecords.tsx';

export function Audit({ api }: { api: PlatformApi }) {
  const [version, setVersion] = useState(0);
  const loaded = useLoad(() => api.verifyChain(), [version]);
  const view = loaded.state === 'ready' ? chain(loaded.value) : null;
  const [showRecords, setShowRecords] = useState(false);

  // Item 1 (third batch) — the Panel stays mounted across a re-verify rather
  // than the whole screen collapsing to a skeleton, so "Verify again" is
  // still there to be disabled while the check it started is running.
  return (
    <>
      <div className="page-head"><h1>{t('nav.audit')}</h1></div>

      <Panel
        title={t('integrity.audit.verification')}
        aside={
          <button
            className="primary"
            disabled={loaded.state === 'loading'}
            onClick={() => setVersion((v) => v + 1)}
          >
            {loaded.state === 'loading' ? t('integrity.audit.verifying') : t('integrity.audit.verifyAgain')}
          </button>
        }
      >
        {loaded.state === 'loading' ? <Loading rows={2} /> : null}
        {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}

        {view ? (
          <>
            {/*
              Batch 2 item 7 — the verdict led with two explanation paragraphs
              above it, which put the actual answer about 1,100px down a phone
              screen. A Figure leads instead: the short answer first, its own
              sentence as the note under it, then the counts that back it up
              (a number nobody can name is not clarity — "513" and "4" said
              nothing about what a record is or a chain is, which is what the
              collapsed explanation below still says).
            */}
            {/* Batch 2 review, finding 4 — two fixes. First, a chain that
                fails verification is a failure (§4's overdue mark), not
                merely something needing a look; `Figure` has no `overdue`
                prop (packages/portal-kit/src/ui.tsx — only `provisional` and
                the boolean `attention`), so this keeps `attention` but makes
                the value itself say "failed", not the ambiguous "N record(s)
                fail". Second, `view.verified` is false both when the chain
                actually fails and when there was nothing to check
                (`emptyWarning` set) — the old value text showed "0 records
                fail" in the empty case, which reads as a clean result rather
                than the untrustworthy one it is. */}
            <Figure
              view={{
                label: t('integrity.audit.result'),
                value: view.emptyWarning
                  ? t('integrity.audit.nothingToVerify')
                  : view.verified
                  ? t('integrity.audit.verifies')
                  : tCount('integrity.audit.failed', view.failures.length, { count: formatCount(view.failures.length) }),
                provisional: false,
                note: view.verdict,
              }}
              // A chain that fails is a failure (the overdue diamond); a check
              // with nothing to check is untrustworthy, not failed (attention).
              {...(view.emptyWarning ? { attention: true } : !view.verified ? { overdue: true } : {})}
            />

            <Facts
              items={[
                // §42.3 — when, first: every other fact here is only as good
                // as the moment it was taken, and this screen's own copy tells
                // the reader to press Verify again after anything they would
                // want to prove later. Without this line a fresh check and a
                // week-old one are the same page.
                ...(view.verifiedAt ? [{ label: t('integrity.audit.checkedAt'), value: view.verifiedAt }] : []),
                // Owner 2026-10-07: the count opens the records behind it that
                // an administrator should know about. A real button inside the
                // card, stretched over it (styles.css .fact-open), so the whole
                // card is the target and the keyboard reaches it.
                {
                  label: t('integrity.audit.recordsChecked'),
                  value: (
                    <button
                      type="button"
                      className="fact-open"
                      aria-haspopup="dialog"
                      onClick={() => setShowRecords(true)}
                    >
                      {view.recordsChecked}
                      <span className="fact-open-hint">{t('integrity.audit.viewRecords')}</span>
                    </button>
                  ),
                },
                { label: t('integrity.audit.chainsChecked'), value: view.chainsChecked },
                { label: t('integrity.audit.failures'), value: formatCount(view.failures.length) },
              ]}
            />

            {view.emptyWarning ? (
              <Callout tone="caution">{view.emptyWarning}</Callout>
            ) : null}

            <details>
              <summary>{t('integrity.audit.howItWorks')}</summary>
              <div className="stack">
                <Callout><Rich text={t('integrity.audit.whatItChecks')} /></Callout>


                <Callout tone="note"><Rich text={t('integrity.audit.whyAgain')} /></Callout>
              </div>
            </details>

            {view.failures.length > 0 ? (
              <Sorted rows={view.failures} by={{ chain_key: (f) => sortableCell(f.chain_key), seq: (f) => sortableCell(f.seq), id: (f) => sortableCell(f.id), failure: (f) => sortableCell(f.failure) }}>
                {(ordered, sort) => (
              <TableScroll>
                <table>
                  <thead>
                    <tr><SortTh sort={sort} col="chain_key">{t('integrity.audit.chain')}</SortTh><SortTh sort={sort} col="seq" align="num">{t('integrity.audit.seq')}</SortTh><SortTh sort={sort} col="id">{t('integrity.audit.record')}</SortTh><SortTh sort={sort} col="failure">{t('integrity.audit.failure')}</SortTh></tr>
                  </thead>
                  <tbody>
                    {ordered.map((f) => (
                      <tr key={f.id}>
                        {/* Batch 2 item 7 — chain and record are IDs, so mono
                            like every other code in this console; and left,
                            under their left-aligned headings (they carried
                            `num`, which right-aligned them under "Chain" and
                            "Record" while nothing else about them is a
                            number). */}
                        <td className="mono">{f.chain_key}</td>
                        <td className="num">{f.seq}</td>
                        <td className="mono faint">{f.id}</td>
                        <td className="wrap">{f.failure}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
                )}
              </Sorted>
            ) : null}
          </>
        ) : null}
      </Panel>
      {showRecords ? <AuditRecordsDialog api={api} onClose={() => setShowRecords(false)} /> : null}
    </>
  );
}
